'use client';

import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import IntroView from '../views/IntroView';
import ChatView from '../views/ChatView';
import FormView from '../views/FormView';
import FormSuccessView from '../views/FormSuccessView';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  originPosition?: { x: number; y: number };
}

export default function ContactModal({ isOpen, onClose, originPosition }: ContactModalProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  // const [message, setMessage] = useState('');
  const [currentView, setCurrentView] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  // Debug flag to keep tooltip visible for styling checks. Set to false to restore interactions.
  const isTooltipDebugAlwaysOn = false;
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const lastActiveElementRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Dispatch event to hide navigation
      window.dispatchEvent(new CustomEvent('contactModalOpen'));
      
      // Reset to IntroView when modal opens
      setCurrentView(1);
      // Reset selected options when modal opens
      setSelectedOptions([]);
      setModalVisible(false);
      
      // Set transform origin to center of screen
      if (modalRef.current) {
        modalRef.current.style.transformOrigin = '50% 50%';
      }
      setTimeout(() => setModalVisible(true), 50);
      
      // Save last active element and focus the close button for SR users
      lastActiveElementRef.current = document.activeElement;

      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      // Focus management
      const toFocus = closeBtnRef.current || modalRef.current;
      toFocus?.focus?.();

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
        if (e.key === 'Tab') {
          // rudimentary focus trap
          const container = modalRef.current;
          if (!container) return;
          const focusable = container.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      };
      document.addEventListener('keydown', handleKeyDown);

      // Prevent background scrolling when modal is open
      const handleWheel = (e: WheelEvent) => {
        const target = e.target as HTMLElement;
        const modal = modalRef.current;
        if (!modal) return;
        
        // If scrolling outside the modal, prevent it
        if (!modal.contains(target)) {
          e.preventDefault();
          return;
        }
        
        // Find the nearest scrollable element by traversing up the DOM tree
        let element: HTMLElement | null = target;
        while (element && element !== modal) {
          const style = window.getComputedStyle(element);
          const isScrollable = 
            element.scrollHeight > element.clientHeight &&
            (style.overflowY === 'auto' || style.overflowY === 'scroll' || 
             element.tagName === 'TEXTAREA');
          
          if (isScrollable) {
            const isScrollingUp = e.deltaY < 0;
            const isScrollingDown = e.deltaY > 0;
            const isAtTop = element.scrollTop <= 0;
            const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
            
            // If we can scroll in this direction, allow it and stop propagation to body
            if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
              e.stopPropagation();
              return;
            }
            // If at boundary, still stop propagation to prevent body scroll
            e.stopPropagation();
            return;
          }
          
          element = element.parentElement;
        }
        
        // If no scrollable element found, prevent background scroll
        e.stopPropagation();
      };
      
      // Use passive: false to allow preventDefault
      document.addEventListener('wheel', handleWheel, { passive: false });
      
      const handleTouchMove = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const modal = modalRef.current;
        if (!modal || !modal.contains(target)) {
          e.preventDefault();
        }
      };
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('wheel', handleWheel);
        document.removeEventListener('touchmove', handleTouchMove);
        // Dispatch event when modal closes
        window.dispatchEvent(new CustomEvent('contactModalClose'));
        // Restore focus to the element that triggered the modal
        if (lastActiveElementRef.current instanceof HTMLElement) {
          lastActiveElementRef.current.focus();
        }
      };
    } else {
      // Reset visibility when modal closes
      setModalVisible(false);
    }
  }, [isOpen, originPosition]);

  if (!isOpen) return null;

  const handleTooltipOpen = () => {
    setIsTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  const handleTooltipToggle = () => {
    setIsTooltipOpen((prev) => !prev);
  };

  const handleTooltipKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTooltipToggle();
    }
    if (event.key === 'Escape') {
      handleTooltipClose();
    }
  };

  const tooltipId = 'contact-modal-ai-tooltip';
  const tooltipVisible = isTooltipDebugAlwaysOn || isTooltipOpen;

  return (
    <div className="contact-modal fixed inset-0 z-[100] flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm overflow-hidden"
        role="button"
        tabIndex={0}
        aria-label="Close contact dialog"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
        onWheel={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      
      <div
        id="contact-modal"
        ref={modalRef}
        className={`relative bg-[#FDFEF6] rounded-3xl border border-gray-900 shadow-2xl w-full max-w-[1300px] mx-4 h-[90dvh] overflow-hidden flex flex-col transition-all duration-300 ease-out ${
          modalVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.01]'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
        tabIndex={-1}
      >
        <div className='flex flex-col justify-between h-full px-3 lg:px-6 pt-4 min-h-0'>
          <div className="flex items-center justify-between">
            <div></div>
            <button
              onClick={onClose}
              ref={closeBtnRef}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
              aria-label="Close contact dialog"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <X size={20} />
            </button>
          </div>

          <h2 id="contact-modal-title" className="sr-only">Contact Havas CX</h2>

          <div className='view-container flex-1 min-h-0'>
            {currentView === 1 && <IntroView setCurrentView={setCurrentView} selectedOptions={selectedOptions} setSelectedOptions={setSelectedOptions} originPosition={originPosition} />}
            {currentView === 2 && <ChatView setCurrentView={setCurrentView} selectedOptions={selectedOptions} />}
            {currentView === 3 && <FormView setCurrentView={setCurrentView} selectedOptions={selectedOptions} />}
            {currentView === 4 && <FormSuccessView />}
          </div>

          {(currentView === 1 || currentView === 2) && (
            <div className="relative flex items-center justify-center gap-2 pb-1 md:pb-3 md:px-3 ">
              <div className="label-xs text-[#666] text-center">
                Responses generated using AI which can make mistakes.
              </div>
              <div
                className="relative flex items-center justify-center"
                onMouseEnter={handleTooltipOpen}
                onMouseLeave={handleTooltipClose}
              >
                <button
                  type="button"
                  aria-label="More information about AI responses"
                  aria-describedby={tooltipVisible ? tooltipId : undefined}
                  aria-expanded={tooltipVisible}
                  className="flex h-[13px] w-[13px] items-center justify-center rounded-full border border-[#8E8E8E] text-[#8E8E8E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  onFocus={handleTooltipOpen}
                  onBlur={handleTooltipClose}
                  onClick={handleTooltipToggle}
                  onKeyDown={handleTooltipKeyDown}
                >
                  <span aria-hidden="true" className="label-xs">
                    i
                  </span>
                </button>

                {tooltipVisible && (
                  <div
                    id={tooltipId}
                    role="tooltip"
                    className="absolute bottom-[25px] md:bottom-9 left-1/2 w-[calc(100vw-32px)] max-w-[360px] -translate-x-[94%] md:-translate-x-[85%] rounded-2xl border border-[#8E8E8E] bg-[#FDFEF6] px-[16px] py-[7px] text-left text-sm text-[#4A4A4A] shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 md:max-w-[440px] md:-translate-x-[75%]"
                  >
                    <div
                      aria-hidden="true"
                      className="absolute -bottom-1 right-[15px] h-2 w-2 -translate-x-[50%] rotate-45 border-b border-r border-[#8E8E8E] bg-white md:right-[57px]"
                    />
                    <p className="label-xs">
                      This chat is for general information about Havas CX US. Please don&apos;t enter personal or
                      confidential data. Responses are generated by AI and may not always be accurate.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}